import React, { useState, useEffect } from "react";

type Card = {
  id: number;
  imageUrl: string;
  isFlipped: boolean;
  isMatched: boolean;
};

type DifficultyLevel = "easy" | "medium" | "hard";

type Props = {};

export default function MemoryMatchGamePage({}: Props) {
  // Game state
  const [cards, setCards] = useState<Card[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [matchedPairs, setMatchedPairs] = useState<number>(0);
  const [moves, setMoves] = useState<number>(0);
  const [gameStarted, setGameStarted] = useState<boolean>(false);
  const [gameCompleted, setGameCompleted] = useState<boolean>(false);
  const [difficulty, setDifficulty] = useState<DifficultyLevel>("medium");
  const [timer, setTimer] = useState<number>(0);
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(
    null
  );
  const [bestScores, setBestScores] = useState<
    Record<DifficultyLevel, number | null>
  >({
    easy: null,
    medium: null,
    hard: null,
  });

  // Card images (using placeholder images with different colors for illustration)
  const cardImages = [
    "🍎",
    "🍌",
    "🍇",
    "🍉",
    "🍊",
    "🍋",
    "🍍",
    "🍒",
    "🍓",
    "🥝",
    "🥥",
    "🍄",
  ];

  // Difficulty settings
  const difficultySettings = {
    easy: { pairs: 6, gridCols: 3 },
    medium: { pairs: 8, gridCols: 4 },
    hard: { pairs: 12, gridCols: 4 },
  };

  // Initialize game
  useEffect(() => {
    // Load best scores from localStorage
    const savedScores = localStorage.getItem("memoryMatchBestScores");
    if (savedScores) {
      setBestScores(JSON.parse(savedScores));
    }
  }, []);

  // Timer effect
  useEffect(() => {
    if (gameStarted && !gameCompleted) {
      const interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer + 1);
      }, 1000);
      setTimerInterval(interval);
      return () => clearInterval(interval);
    } else if (timerInterval) {
      clearInterval(timerInterval);
    }
  }, [gameStarted, gameCompleted]);

  // Check for game completion
  useEffect(() => {
    if (gameStarted && matchedPairs === difficultySettings[difficulty].pairs) {
      setGameCompleted(true);
      setGameStarted(false);

      // Update best score
      const currentScore = calculateScore();
      const currentBest = bestScores[difficulty];

      if (currentBest === null || currentScore > currentBest) {
        const newBestScores = { ...bestScores, [difficulty]: currentScore };
        setBestScores(newBestScores);
        localStorage.setItem(
          "memoryMatchBestScores",
          JSON.stringify(newBestScores)
        );
      }
    }
  }, [matchedPairs, difficulty, gameStarted]);

  // Check for matches when two cards are flipped
  useEffect(() => {
    if (flippedCards.length === 2) {
      const [firstCardId, secondCardId] = flippedCards;
      const firstCard = cards.find((card) => card.id === firstCardId);
      const secondCard = cards.find((card) => card.id === secondCardId);

      if (
        firstCard &&
        secondCard &&
        firstCard.imageUrl === secondCard.imageUrl
      ) {
        // Match found
        setCards(
          cards.map((card) =>
            card.id === firstCardId || card.id === secondCardId
              ? { ...card, isMatched: true }
              : card
          )
        );
        setMatchedPairs(matchedPairs + 1);
        setFlippedCards([]);
      } else {
        // No match
        setTimeout(() => {
          setCards(
            cards.map((card) =>
              flippedCards.includes(card.id)
                ? { ...card, isFlipped: false }
                : card
            )
          );
          setFlippedCards([]);
        }, 1000);
      }
      setMoves(moves + 1);
    }
  }, [flippedCards, cards, matchedPairs, moves]);

  // Initialize game based on difficulty
  const initializeGame = () => {
    const numPairs = difficultySettings[difficulty].pairs;
    const gameImages = cardImages.slice(0, numPairs);

    // Create pairs of cards
    let newCards: Card[] = [];
    gameImages.forEach((image, index) => {
      // Create two cards with the same image
      const card1: Card = {
        id: index * 2,
        imageUrl: image,
        isFlipped: false,
        isMatched: false,
      };
      const card2: Card = {
        id: index * 2 + 1,
        imageUrl: image,
        isFlipped: false,
        isMatched: false,
      };
      newCards.push(card1, card2);
    });

    // Shuffle cards
    newCards = shuffleArray(newCards);

    setCards(newCards);
    setFlippedCards([]);
    setMatchedPairs(0);
    setMoves(0);
    setTimer(0);
    setGameStarted(true);
    setGameCompleted(false);
  };

  // Shuffle array (Fisher-Yates algorithm)
  const shuffleArray = (array: Card[]): Card[] => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  // Handle card click
  const handleCardClick = (id: number) => {
    // Ignore clicks if two cards are already flipped or the card is already flipped or matched
    const clickedCard = cards.find((card) => card.id === id);
    if (
      flippedCards.length >= 2 ||
      !clickedCard ||
      clickedCard.isFlipped ||
      clickedCard.isMatched
    ) {
      return;
    }

    // Flip the card
    setCards(
      cards.map((card) =>
        card.id === id ? { ...card, isFlipped: true } : card
      )
    );

    // Add card to flipped cards
    setFlippedCards([...flippedCards, id]);
  };

  // Format time (seconds to MM:SS)
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  // Calculate score based on moves, time, and difficulty
  const calculateScore = (): number => {
    const baseScore = 1000;
    const difficultyMultiplier = {
      easy: 1,
      medium: 1.5,
      hard: 2,
    };

    // Lower moves and time mean higher score
    const movesPenalty = moves * 10;
    const timePenalty = timer * 2;

    const calculatedScore = Math.max(
      0,
      Math.floor(
        (baseScore - movesPenalty - timePenalty) *
          difficultyMultiplier[difficulty]
      )
    );

    return calculatedScore;
  };

  // Restart the game
  const handleRestart = () => {
    initializeGame();
  };

  // Change difficulty
  const handleDifficultyChange = (newDifficulty: DifficultyLevel) => {
    setDifficulty(newDifficulty);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "24px",
        fontFamily: "Arial, sans-serif",
        maxWidth: "1200px",
        margin: "0 auto",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
      }}
    >
      <h1
        style={{
          fontSize: "2.5rem",
          marginBottom: "16px",
          color: "#2c3e50",
          textShadow: "2px 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        Memory Match
      </h1>

      {/* Game Controls */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          maxWidth: "800px",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span style={{ fontWeight: "bold", color: "#34495e" }}>
            Difficulty:
          </span>
          <div
            style={{
              display: "flex",
              gap: "8px",
            }}
          >
            {(["easy", "medium", "hard"] as DifficultyLevel[]).map((level) => (
              <button
                key={level}
                onClick={() => handleDifficultyChange(level)}
                style={{
                  padding: "6px 12px",
                  borderRadius: "16px",
                  border: "none",
                  background: difficulty === level ? "#3498db" : "#e0e0e0",
                  color: difficulty === level ? "white" : "#333",
                  fontWeight: difficulty === level ? "bold" : "normal",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={gameStarted ? handleRestart : initializeGame}
          style={{
            padding: "8px 16px",
            borderRadius: "8px",
            border: "none",
            background: "#2ecc71",
            color: "white",
            fontWeight: "bold",
            cursor: "pointer",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
            transition: "all 0.2s ease",
          }}
        >
          {gameStarted ? "Restart Game" : "Start New Game"}
        </button>
      </div>

      {/* Game Info */}
      {gameStarted || gameCompleted ? (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            maxWidth: "800px",
            marginBottom: "24px",
            padding: "12px 20px",
            borderRadius: "12px",
            background: "rgba(255, 255, 255, 0.8)",
            boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: "1.1rem",
                fontWeight: "bold",
                color: "#34495e",
              }}
            >
              Moves
            </div>
            <div style={{ fontSize: "1.5rem", color: "#2c3e50" }}>{moves}</div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: "1.1rem",
                fontWeight: "bold",
                color: "#34495e",
              }}
            >
              Time
            </div>
            <div style={{ fontSize: "1.5rem", color: "#2c3e50" }}>
              {formatTime(timer)}
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: "1.1rem",
                fontWeight: "bold",
                color: "#34495e",
              }}
            >
              Pairs
            </div>
            <div style={{ fontSize: "1.5rem", color: "#2c3e50" }}>
              {matchedPairs}/{difficultySettings[difficulty].pairs}
            </div>
          </div>
        </div>
      ) : (
        <div
          style={{
            width: "100%",
            maxWidth: "800px",
            marginBottom: "24px",
            padding: "24px",
            borderRadius: "12px",
            background: "rgba(255, 255, 255, 0.8)",
            boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
            textAlign: "center",
          }}
        >
          <h2 style={{ marginTop: 0, color: "#2c3e50" }}>How to Play</h2>
          <p
            style={{ fontSize: "1.1rem", lineHeight: "1.6", color: "#34495e" }}
          >
            Flip cards to find matching pairs. Match all pairs as quickly as
            possible with the fewest moves to get the highest score!
          </p>
          <p
            style={{ fontSize: "1.1rem", lineHeight: "1.6", color: "#34495e" }}
          >
            Select a difficulty level and click "Start New Game" to begin.
          </p>
        </div>
      )}

      {/* Game Completed Message */}
      {gameCompleted && (
        <div
          style={{
            width: "100%",
            maxWidth: "800px",
            marginBottom: "24px",
            padding: "24px",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            textAlign: "center",
            boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
            animation: "fadeIn 0.5s ease-in-out",
          }}
        >
          <h2 style={{ marginTop: 0, fontSize: "2rem" }}>
            Congratulations! 🎉
          </h2>
          <p style={{ fontSize: "1.2rem", marginBottom: "8px" }}>
            You completed the game in {formatTime(timer)} with {moves} moves!
          </p>
          <div
            style={{ fontSize: "2.5rem", fontWeight: "bold", margin: "16px 0" }}
          >
            Score: {calculateScore()}
          </div>
          {bestScores[difficulty] !== null && (
            <p style={{ fontSize: "1.1rem" }}>
              Best Score ({difficulty}): {bestScores[difficulty]}
            </p>
          )}
          <button
            onClick={handleRestart}
            style={{
              padding: "12px 24px",
              borderRadius: "8px",
              border: "none",
              background: "white",
              color: "#764ba2",
              fontWeight: "bold",
              fontSize: "1.1rem",
              cursor: "pointer",
              marginTop: "16px",
              boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              transition: "all 0.2s ease",
            }}
          >
            Play Again
          </button>
        </div>
      )}

      {/* Game Board */}
      {(gameStarted || gameCompleted) && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${difficultySettings[difficulty].gridCols}, 1fr)`,
            gap: "12px",
            width: "100%",
            maxWidth: "800px",
          }}
        >
          {cards.map((card) => (
            <div
              key={card.id}
              onClick={() => handleCardClick(card.id)}
              style={{
                position: "relative",
                aspectRatio: "1/1",
                transform: card.isFlipped ? "rotateY(180deg)" : "rotateY(0)",
                transformStyle: "preserve-3d",
                transition: "transform 0.6s",
                cursor: card.isMatched ? "default" : "pointer",
              }}
            >
              {/* Card Back */}
              <div
                style={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  borderRadius: "8px",
                  background:
                    "linear-gradient(135deg, #3498db 0%, #2980b9 100%)",
                  backfaceVisibility: "hidden",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  fontSize: "2rem",
                  color: "white",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                }}
              >
                ?
              </div>

              {/* Card Front */}
              <div
                style={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  borderRadius: "8px",
                  background: "white",
                  transform: "rotateY(180deg)",
                  backfaceVisibility: "hidden",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  border: card.isMatched
                    ? "3px solid #2ecc71"
                    : "1px solid #e0e0e0",
                }}
              >
                <div
                  style={{
                    width: "80%",
                    height: "80%",
                    backgroundImage: `url('${card.imageUrl}')`,
                    backgroundSize: "contain",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    filter: card.isMatched ? "none" : "none",
                  }}
                >
                  <p style={{ fontSize: "30px", color: "#2c3e50" }}>
                    {card.imageUrl}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Best Scores */}
      <div
        style={{
          width: "100%",
          maxWidth: "800px",
          marginTop: "32px",
          padding: "16px 24px",
          borderRadius: "12px",
          background: "rgba(255, 255, 255, 0.6)",
          boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
        }}
      >
        <h3
          style={{
            fontSize: "1.2rem",
            color: "#2c3e50",
            borderBottom: "1px solid #e0e0e0",
            paddingBottom: "8px",
            marginTop: 0,
          }}
        >
          Best Scores
        </h3>
        <div
          style={{
            display: "flex",
            justifyContent: "space-around",
            marginTop: "8px",
          }}
        >
          {Object.entries(bestScores).map(([level, score]) => (
            <div key={level} style={{ textAlign: "center" }}>
              <div style={{ fontWeight: "bold", color: "#34495e" }}>
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </div>
              <div style={{ fontSize: "1.2rem", color: "#2c3e50" }}>
                {score !== null ? score : "-"}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Additional CSS for animations */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          button:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 8px rgba(0,0,0,0.15);
          }
          
          button:active {
            transform: translateY(0);
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
        `}
      </style>
    </div>
  );
}
